import React from 'react';
import { faHeartbeat } from '@fortawesome/free-solid-svg-icons';
import CrudManager from '../../../components/common/CrudManager';
import CareLinkService from '../../../services/CareLinkService';
import CareLinkFormContent, {
  emptyCareCenter,
  emptyDoctor,
} from './CareLinkFormContent';
import { formatDateTimeToDisplay } from '../../../utils/dateHelper';
import './CareLinkManage.css';

const hasText = (value) => typeof value === 'string' && value.trim() !== '';

const isDoctorFilled = (doctor) => {
  if (!doctor) return false;
  return ['name', 'phone', 'medicalCenter', 'specialty'].some((key) => hasText(doctor[key]));
};

const isCareCenterFilled = (careCenter) => {
  if (!careCenter) return false;
  // Type alone must not force a care center (person may live at home).
  return ['name', 'phone', 'address', 'city', 'postalCode', 'country'].some((key) =>
    hasText(careCenter[key])
  );
};

const buildCareLinkPayload = (formData) => {
  const contacts = (Array.isArray(formData.contacts) ? formData.contacts : [])
    .filter((contact) => hasText(contact?.name) || hasText(contact?.phone))
    .map((contact) => ({
      ...contact,
      primaryContact: Boolean(contact.primaryContact),
      emergencyContact: contact.emergencyContact !== false,
      enabled: contact.enabled !== false,
    }));

  const medications = (Array.isArray(formData.medications) ? formData.medications : [])
    .filter((medication) => hasText(medication?.name))
    .map((medication) => ({
      ...medication,
      active: medication.active !== false,
    }));

  const doctor = isDoctorFilled(formData.doctor)
    ? {
        name: formData.doctor.name?.trim() || '',
        phone: formData.doctor.phone?.trim() || '',
        medicalCenter: formData.doctor.medicalCenter || null,
        specialty: formData.doctor.specialty || null,
      }
    : null;

  const careCenter = isCareCenterFilled(formData.careCenter)
    ? {
        name: formData.careCenter.name?.trim() || '',
        type: formData.careCenter.type || null,
        address: formData.careCenter.address || null,
        city: formData.careCenter.city || null,
        postalCode: formData.careCenter.postalCode || null,
        country: formData.careCenter.country || null,
        phone: formData.careCenter.phone || null,
      }
    : null;

  return {
    firstName: formData.firstName?.trim() || '',
    lastName: formData.lastName?.trim() || '',
    secondLastName: formData.secondLastName || null,
    dateOfBirth: formData.dateOfBirth || null,
    identificationNumber: formData.identificationNumber || null,
    identificationNumberPublic: Boolean(formData.identificationNumberPublic),
    socialSecurityNumber: formData.socialSecurityNumber || null,
    healthCardNumber: formData.healthCardNumber || null,
    healthCardNumberPublic: Boolean(formData.healthCardNumberPublic),
    bloodTypeEnum: formData.bloodTypeEnum || null,
    preferredLanguage: formData.preferredLanguage || null,
    criticalInformation: formData.criticalInformation || null,
    medicalConditions: formData.medicalConditions || null,
    foodAllergies: formData.foodAllergies || null,
    medicationAllergies: formData.medicationAllergies || null,
    relevantMedicalInformation: formData.relevantMedicalInformation || null,
    hasDisability: Boolean(formData.hasDisability),
    disabilityType: formData.disabilityType || null,
    communicationTypeEnum: formData.communicationTypeEnum || null,
    communicationInstructions: formData.communicationInstructions || null,
    assistanceInstructions: formData.assistanceInstructions || null,
    thingsToAvoid: formData.thingsToAvoid || null,
    preferences: formData.preferences || null,
    requiresSupervision: Boolean(formData.requiresSupervision),
    supervisionInstructions: formData.supervisionInstructions || null,
    emergencyInstructions: formData.emergencyInstructions || null,
    importantInformation: formData.importantInformation || null,
    profilePhotoUrl: formData.profilePhotoUrl || null,
    enabled: formData.enabled !== false,
    doctor,
    careCenter,
    contacts,
    medications,
  };
};

const validateCareLinkForm = (formData, t) => {
  const errors = {};

  if (!hasText(formData?.firstName)) {
    errors.firstName = t('carelink.errors.firstNameRequired');
  }
  if (!hasText(formData?.lastName)) {
    errors.lastName = t('carelink.errors.lastNameRequired');
  }

  const contacts = Array.isArray(formData?.contacts) ? formData.contacts : [];
  const incompleteContact = contacts.some((contact) => {
    const started = hasText(contact?.name) || hasText(contact?.phone) || hasText(contact?.secondaryPhone);
    if (!started) return false;
    return !hasText(contact?.name) || !hasText(contact?.phone) || !contact?.role;
  });
  if (incompleteContact) {
    errors.contacts = t('carelink.errors.contactIncomplete');
  }

  const medications = Array.isArray(formData?.medications) ? formData.medications : [];
  const incompleteMedication = medications.some((medication) => {
    const started = Object.values(medication || {}).some((value) => hasText(String(value ?? '')));
    return started && !hasText(medication?.name);
  });
  if (incompleteMedication) {
    errors.medications = t('carelink.errors.medicationIncomplete');
  }

  if (isDoctorFilled(formData?.doctor)) {
    if (!hasText(formData.doctor.name) || !hasText(formData.doctor.phone)) {
      errors.doctor = t('carelink.errors.doctorIncomplete');
    }
  }

  if (
    (isCareCenterFilled(formData?.careCenter) || hasText(formData?.careCenter?.type))
    && !hasText(formData?.careCenter?.name)
  ) {
    errors.careCenter = t('carelink.errors.careCenterIncomplete');
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

const CareLinkManage = () => {
  const columns = [
    {
      key: 'firstName',
      label: 'carelink.firstName',
      sortable: true,
      showInForm: true,
      required: true,
      showInGrid: true,
      showInFilter: true,
      filterSize: 'medium',
    },
    {
      key: 'lastName',
      label: 'carelink.lastName',
      sortable: true,
      showInForm: true,
      required: true,
      showInGrid: true,
      showInFilter: true,
      filterSize: 'medium',
    },
    {
      key: 'secondLastName',
      label: 'carelink.secondLastName',
      sortable: true,
      showInForm: false,
      showInGrid: true,
      showInFilter: true,
      filterSize: 'medium',
    },
    {
      key: 'publicToken',
      label: 'carelink.publicToken',
      sortable: false,
      showInForm: false,
      showInGrid: true,
      showInFilter: true,
      filterSize: 'large',
    },
    {
      key: 'bloodTypeEnum',
      label: 'carelink.bloodType',
      sortable: true,
      showInForm: false,
      showInGrid: false,
      render: (row) => row.bloodTypeEnum || '',
    },
    {
      key: 'requiresSupervision',
      label: 'carelink.requiresSupervision',
      sortable: true,
      showInForm: false,
      showInGrid: false,
    },
    {
      key: 'registrationDate',
      label: 'carelink.registrationDate',
      sortable: true,
      showInForm: false,
      showInGrid: false,
      className: 'grid-date',
      render: (row) => (row.registrationDate ? formatDateTimeToDisplay(row.registrationDate) : ''),
    },
    {
      key: 'enabled',
      label: 'carelink.enabled',
      showInGrid: false,
      sortable: false,
      showInFilter: true,
      filterType: 'checkbox',
      filterSize: 'small',
    },
  ];

  return (
    <CrudManager
      service={CareLinkService}
      columns={columns}
      entityName="carelink"
      icon={faHeartbeat}
      formLayout="inline"
      initialFormData={{
        firstName: '',
        lastName: '',
        secondLastName: '',
        dateOfBirth: '',
        identificationNumber: '',
        identificationNumberPublic: false,
        socialSecurityNumber: '',
        healthCardNumber: '',
        healthCardNumberPublic: false,
        bloodTypeEnum: '',
        preferredLanguage: '',
        criticalInformation: '',
        medicalConditions: '',
        foodAllergies: '',
        medicationAllergies: '',
        relevantMedicalInformation: '',
        hasDisability: false,
        disabilityType: '',
        communicationTypeEnum: '',
        communicationInstructions: '',
        assistanceInstructions: '',
        thingsToAvoid: '',
        preferences: '',
        requiresSupervision: false,
        supervisionInstructions: '',
        emergencyInstructions: '',
        importantInformation: '',
        profilePhotoUrl: '',
        enabled: true,
        doctor: emptyDoctor(),
        careCenter: emptyCareCenter(),
        contacts: [],
        medications: [],
      }}
      validateForm={validateCareLinkForm}
      onBeforeCreate={async (formData) => buildCareLinkPayload(formData)}
      onBeforeUpdate={async (formData) => buildCareLinkPayload(formData)}
      onBeforeEdit={(item) => ({
        ...item,
        dateOfBirth: item.dateOfBirth || '',
        bloodTypeEnum: item.bloodTypeEnum || '',
        communicationTypeEnum: item.communicationTypeEnum || '',
        identificationNumberPublic: Boolean(item.identificationNumberPublic),
        healthCardNumberPublic: Boolean(item.healthCardNumberPublic),
        doctor: item.doctor || emptyDoctor(),
        careCenter: item.careCenter || emptyCareCenter(),
        contacts: Array.isArray(item.contacts) ? item.contacts : [],
        medications: Array.isArray(item.medications) ? item.medications : [],
      })}
      renderForm={({ formData, onChange, isEditing, fieldErrors }) => (
        <CareLinkFormContent
          formData={formData}
          onChange={onChange}
          isEditing={isEditing}
          fieldErrors={fieldErrors}
        />
      )}
    />
  );
};

export default CareLinkManage;
